import { Flex, Text, Link, Heading, Divider, Spinner } from '@customerio/pluma-components/react';
import plur from 'plur';
import type { SearchResult } from '@/services/auth/types';
import { formatNumber } from '@/utils/number';
import { isEmptyString } from '@/utils/string';
import * as dialogStyles from '../index.css';
import { CampaignPreview } from './campaign-preview';
import { SegmentPreview } from './segment-preview';
import { TemplatePreview } from './template-preview';
import { ActionPreview } from './action-preview';
import { NewsletterPreview } from './newsletter-preview';
import { GoalPreview } from './goal-preview';
import { TransactionalMessagePreview } from './transactional-message-preview';
import { CustomerPreview } from './customer-preview';
import { DocsPreview } from './docs-preview';
import { IntegrationPreview } from './integration-preview';
import { FormPreview } from './form-preview';
import { ObjectTypePreview } from './object-type-preview';
import type { IntegrationCatalog } from '../utils/integration-catalog';
import { hasDescriptionField, SEARCH_RESULT_KIND_METADATA } from '../utils/result-metadata';

type PreviewContentProps = {
  result: SearchResult;
  integrationCatalog: IntegrationCatalog;
};

// This is intentionally an exhaustive discriminated-union renderer; the switch
// preserves SearchResult narrowing better than a dispatch table.
// fallow-ignore-next-line complexity
function PreviewContent({ result, integrationCatalog }: PreviewContentProps): React.JSX.Element | null {
  switch (result.kind) {
    case 'campaign':
      return <CampaignPreview result={result} />;
    case 'segment':
      return <SegmentPreview result={result} />;
    case 'template':
      return <TemplatePreview result={result} />;
    case 'action':
      return <ActionPreview result={result} />;
    case 'newsletter':
      return <NewsletterPreview result={result} />;
    case 'goal':
      return <GoalPreview result={result} />;
    case 'transactional_message':
      return <TransactionalMessagePreview result={result} />;
    case 'customer':
      return <CustomerPreview result={result} />;
    case 'docs':
      return <DocsPreview result={result} />;
    case 'source':
    case 'destination':
      return <IntegrationPreview result={result} catalog={integrationCatalog} />;
    case 'custom_form':
      return <FormPreview result={result} />;
    case 'object_type':
      return <ObjectTypePreview result={result} />;
    case 'navigation':
    case 'ad_audience':
    case 'reporting_webhook':
    case 'cdp_out':
    case 'data_out':
      return null;
    default:
      return null;
  }
}

type DescriptionProps = { result: SearchResult };

function Description({ result }: DescriptionProps) {
  // docs renders its description inside DocsPreview; skip the generic row here.
  if (result.kind === 'docs') return null;
  if (hasDescriptionField(result)) {
    return <DescriptionText value={result.other.description} />;
  }
  if (result.kind === 'action') {
    return <ActionCampaignDescription result={result} />;
  }
  return null;
}

type DescriptionTextProps = { value?: string };

function DescriptionText({ value }: DescriptionTextProps) {
  const empty = isEmptyString(value);
  return (
    <Text size="xs" color={empty ? 'placeholder' : 'subtle'} className={empty ? undefined : 'fs-mask'}>
      {empty ? '—' : value}
    </Text>
  );
}

function parseCampaignPathname(url: string | undefined): string | null {
  if (isEmptyString(url)) return null;
  try {
    return new URL(url).pathname;
  } catch {
    return null;
  }
}

type ActionCampaignDescriptionProps = { result: Extract<SearchResult, { kind: 'action' }> };

function ActionCampaignDescription({ result }: ActionCampaignDescriptionProps) {
  const { campaign_name, campaign_url } = result.other;
  if (isEmptyString(campaign_name)) return null;
  const campaignPath = parseCampaignPathname(campaign_url);
  return (
    <Text size="xs" color="subtle">
      from Campaign <CampaignNameLink href={campaignPath} name={campaign_name} />
    </Text>
  );
}

type CampaignNameLinkProps = { href: string | null; name: string };

function CampaignNameLink({ href, name }: CampaignNameLinkProps) {
  if (href !== null) {
    return (
      <Link href={href} className="fs-mask">
        {name}
      </Link>
    );
  }
  return (
    <Text as="span" size="xs" className="fs-mask">
      {name}
    </Text>
  );
}

type QuickLookPanelProps = {
  isLoading?: boolean;
  result: SearchResult | null;
  integrationCatalog: IntegrationCatalog;
};

function getPreviewTitle(result: SearchResult): string {
  if (result.kind === 'object_type') {
    if (result.other.object_count === 0) return result.title;

    const objectTypeName = plur(result.other.singular_name, result.title, result.other.object_count);
    return `${formatNumber(result.other.object_count)} ${objectTypeName}`;
  }

  return result.title;
}

export function QuickLookPanel({ isLoading = false, result, integrationCatalog }: QuickLookPanelProps) {
  // Only show the loading spinner when we have no result yet — if a result is already
  // visible, keep showing it rather than flashing a spinner during background fetches
  // (e.g. navigation's 100ms delay on new queries, or eager-source refetches).
  if (isLoading && result == null) {
    return (
      <Flex direction="column" w="full" className={dialogStyles.quickLookPanelShell}>
        <Flex direction="column" alignItems="center" justifyContent="center" flex={1} w="full" p="200">
          <Spinner className={dialogStyles.spinner} color="information" />
        </Flex>
      </Flex>
    );
  }
  if (result == null || !(SEARCH_RESULT_KIND_METADATA[result.kind]?.hasQuickLookPreview ?? false)) {
    return (
      <Flex direction="column" w="full" className={dialogStyles.quickLookPanelShell}>
        <Flex direction="column" alignItems="center" justifyContent="center" flex={1} w="full" p="200">
          <Text size="xs" color="subtle">
            No preview
          </Text>
        </Flex>
      </Flex>
    );
  }

  return (
    <Flex className={dialogStyles.quickLookPanelShell} overflow="hidden">
      <Flex direction="column" gap="200" w="full" flex={1} minH="0" overflow="auto" p="200">
        <Flex direction="column" gap="050">
          <Heading level="2" className="fs-mask">
            {getPreviewTitle(result)}
          </Heading>
          <Description result={result} />
        </Flex>

        <Divider />

        <Flex direction="column" gap="100" w="full" alignItems="stretch">
          <PreviewContent result={result} integrationCatalog={integrationCatalog} />
        </Flex>
      </Flex>
    </Flex>
  );
}
